import { IOrgServiceTypeWithServices, IOrgServicesGroupedByType, IOrgSubService } from "@/api_framework/api_modals/Org";
import {
  IBookingCartPreviewCartItem,
  IBookingCartPreviewRequestCartItem,
  IBookingSummaryItem,
  IBookingSummaryResponseData,
} from "@/api_framework/api_modals/Bookings";

type SubServiceData = {
  sub_service_id: string;
  sub_service_name: string;
  duration: string;
  service_id: string;
};

export type BookingServiceMaps = {
  serviceIdToServiceTypeIdMap: Record<string, string>;
  subServiceToServiceIdMap: Record<string, string>;
  serviceTypeToServiceIdsMap: Record<string, string[]>;
  subServicesByServiceTypeIdMap: Record<string, Array<{ sub_service_id: string; sub_service_name: string; duration: string }>>;
  serviceTypeNamesByIdMap: Record<string, string>;
  subServiceByIdMap: Record<string, SubServiceData>;
};

export type MinimalSelectItem = {
  id: string;
  title: string;
};

export type MinimalSelectedServiceTypeItem = {
  id: string;
  subSelectItems?: Array<{ id: string }>;
};

export type EquipmentServicePayloadItem = {
  service_id: string;
  sub_service_ids: string[];
};

export type EquipmentDetailsServiceItem = {
  service_id: string;
  sub_services_data: string[];
};

export type EquipmentServiceTypeSelectItem = {
  id: string;
  title: string;
  checkboxVisible: boolean;
  withSubItems: boolean;
  subSelectItems: MinimalSelectItem[];
};

type FlatServiceShape = {
  service_id: string;
  service_name?: string;
  service_type_id?: string;
  service_type_name?: string;
  sub_services?: IOrgSubService[];
};

type ServiceSource = FlatServiceShape[] | IOrgServiceTypeWithServices[] | IOrgServicesGroupedByType | null | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object";

const isGroupedServiceType = (value: unknown): value is IOrgServiceTypeWithServices => {
  if (!isRecord(value)) return false;
  return typeof value.id === "string" && typeof value.name === "string" && Array.isArray(value.services);
};

const isFlatService = (value: unknown): value is FlatServiceShape => {
  if (!isRecord(value)) return false;
  return typeof value.service_id === "string";
};

const dedupeSubServices = <T extends { sub_service_id: string }>(subServices: T[] = []): T[] =>
  Array.from(new Map(subServices.map((subService) => [subService.sub_service_id, subService])).values());

export const normalizeServiceTypesFromAnySource = (serviceSource: ServiceSource): IOrgServiceTypeWithServices[] => {
  if (!serviceSource) return [];

  if (Array.isArray(serviceSource)) {
    const groupedServiceTypes = serviceSource.filter(isGroupedServiceType);
    if (groupedServiceTypes.length > 0) return groupedServiceTypes;

    const groupedByServiceType: Record<
      string,
      {
        id: string;
        name: string;
        servicesMap: Record<
          string,
          {
            service_id: string;
            service_name: string;
            sub_services: Array<{ sub_service_id: string; sub_service_name: string; duration: string }>;
          }
        >;
      }
    > = {};

    serviceSource.filter(isFlatService).forEach((service) => {
      const serviceTypeId = service.service_type_id || service.service_id;
      if (!serviceTypeId || !service.service_id) return;
      if (!groupedByServiceType[serviceTypeId]) {
        groupedByServiceType[serviceTypeId] = {
          id: serviceTypeId,
          name: service.service_type_name || service.service_name || "",
          servicesMap: {},
        };
      }
      if (!groupedByServiceType[serviceTypeId].servicesMap[service.service_id]) {
        groupedByServiceType[serviceTypeId].servicesMap[service.service_id] = {
          service_id: service.service_id,
          service_name: service.service_name || "",
          sub_services: [],
        };
      }

      const existingService = groupedByServiceType[serviceTypeId].servicesMap[service.service_id];
      const mergedSubServices = [...(existingService.sub_services ?? []), ...(service.sub_services ?? [])].map((subService) => ({
        sub_service_id: subService.sub_service_id,
        sub_service_name: subService.sub_service_name,
        duration: subService.duration || "",
      }),
      );
      existingService.sub_services = dedupeSubServices(mergedSubServices).filter((subService) => !!subService?.sub_service_id);
    });

    return Object.values(groupedByServiceType).map((serviceType) => ({
      id: serviceType.id,
      name: serviceType.name,
      services: Object.values(serviceType.servicesMap),
    }));
  }

  return Object.values(serviceSource.service_types ?? {});
};

export const buildServiceTypeDropdownOptions = (serviceSource: ServiceSource) => {
  const serviceTypes = normalizeServiceTypesFromAnySource(serviceSource);
  const maps = buildBookingServiceMaps(serviceTypes);
  const selectItems: EquipmentServiceTypeSelectItem[] = serviceTypes.map((serviceType) => ({
    id: serviceType.id,
    title: serviceType.name,
    checkboxVisible: true,
    withSubItems: true,
    subSelectItems: (maps.subServicesByServiceTypeIdMap[serviceType.id] ?? []).map((subService) => ({
      id: subService.sub_service_id,
      title: subService.sub_service_name,
    })),
  }));

  return { serviceTypes, maps, selectItems };
};

export const createServiceTypeSelectionHelper = (serviceSource: ServiceSource) => {
  const { serviceTypes, maps, selectItems } = buildServiceTypeDropdownOptions(serviceSource);

  const toPayloadByServiceId = (selectedServiceTypes: MinimalSelectedServiceTypeItem[] = []): EquipmentServicePayloadItem[] => {
    const servicePayloadMap: Record<string, string[]> = {};

    selectedServiceTypes.forEach((serviceType) => {
      (serviceType?.subSelectItems ?? []).forEach((subService) => {
        const serviceId = maps.subServiceToServiceIdMap[subService?.id];
        if (!serviceId) return;
        if (!servicePayloadMap[serviceId]) servicePayloadMap[serviceId] = [];
        if (!servicePayloadMap[serviceId].includes(subService.id)) {
          servicePayloadMap[serviceId].push(subService.id);
        }
      });
    });

    return Object.entries(servicePayloadMap).map(([serviceId, subServiceIds]) => ({
      service_id: serviceId,
      sub_service_ids: subServiceIds,
    }));
  };

  const toDefaultSelectedByDetails = (
    equipmentServicesData: EquipmentDetailsServiceItem[] = [],
    items: EquipmentServiceTypeSelectItem[] = selectItems,
  ) =>
    items.reduce((final, serviceTypeItem) => {
      const serviceIdsForType = equipmentServicesData
        ?.filter((serviceData) => maps.serviceIdToServiceTypeIdMap[serviceData?.service_id] === serviceTypeItem?.id)
        ?.map((serviceData) => serviceData?.service_id);

      if (!serviceIdsForType || serviceIdsForType.length === 0) return final;

      const selectedSubServiceIds = equipmentServicesData
        ?.filter((serviceData) => serviceIdsForType.includes(serviceData?.service_id))
        ?.flatMap((serviceData) => serviceData?.sub_services_data ?? []);

      const dedupedSelectedSubServiceIds = [...new Set(selectedSubServiceIds)];
      const newsubitems = (serviceTypeItem?.subSelectItems ?? []).filter((subItem) =>
        dedupedSelectedSubServiceIds.includes(subItem?.id),
      );
      const { subSelectItems, ...rest } = serviceTypeItem;
      final.push({ ...rest, subSelectItems: newsubitems || [] });
      return final;
    }, [] as Array<Pick<EquipmentServiceTypeSelectItem, "id" | "title" | "checkboxVisible" | "withSubItems" | "subSelectItems">>);

  return {
    serviceTypes,
    maps,
    selectItems,
    toPayloadByServiceId,
    toDefaultSelectedByDetails,
  };
};

export const buildBookingServiceMaps = (serviceTypes: IOrgServiceTypeWithServices[] = []): BookingServiceMaps => {
  const serviceIdToServiceTypeIdMap: Record<string, string> = {};
  const subServiceToServiceIdMap: Record<string, string> = {};
  const serviceTypeToServiceIdsMap: Record<string, string[]> = {};
  const subServicesByServiceTypeIdMap: Record<
    string,
    Array<{ sub_service_id: string; sub_service_name: string; duration: string }>
  > = {};
  const serviceTypeNamesByIdMap: Record<string, string> = {};
  const subServiceByIdMap: Record<string, SubServiceData> = {};

  serviceTypes.forEach((serviceType) => {
    serviceTypeNamesByIdMap[serviceType.id] = serviceType.name;
    const serviceIds = Array.from(new Set((serviceType.services ?? []).map((service) => service.service_id).filter(Boolean)));
    serviceTypeToServiceIdsMap[serviceType.id] = serviceIds;

    (serviceType.services ?? []).forEach((service) => {
      serviceIdToServiceTypeIdMap[service.service_id] = serviceType.id;
      (service.sub_services ?? []).forEach((subService) => {
        subServiceToServiceIdMap[subService.sub_service_id] = service.service_id;
        subServiceByIdMap[subService.sub_service_id] = {
          sub_service_id: subService.sub_service_id,
          sub_service_name: subService.sub_service_name,
          duration: subService.duration,
          service_id: service.service_id,
        };
      });
    });

    const allSubServices = (serviceType.services ?? []).flatMap((service) => service.sub_services ?? []);
    subServicesByServiceTypeIdMap[serviceType.id] = Array.from(
      new Map(allSubServices.map((subService) => [subService.sub_service_id, subService])).values(),
    );
  });

  return {
    serviceIdToServiceTypeIdMap,
    subServiceToServiceIdMap,
    serviceTypeToServiceIdsMap,
    subServicesByServiceTypeIdMap,
    serviceTypeNamesByIdMap,
    subServiceByIdMap,
  };
};

export const resolveBookingServiceSelection = (options: {
  maps: BookingServiceMaps;
  selectedServiceKey?: string;
  selectedSubServiceId?: string;
}) => {
  const { maps, selectedServiceKey, selectedSubServiceId } = options;
  const selectedSubService = selectedSubServiceId ? maps.subServiceByIdMap[selectedSubServiceId] : null;

  let selectedServiceTypeId = "";
  if (selectedServiceKey && maps.serviceTypeToServiceIdsMap[selectedServiceKey]) {
    selectedServiceTypeId = selectedServiceKey;
  } else if (selectedSubService?.service_id && maps.serviceIdToServiceTypeIdMap[selectedSubService.service_id]) {
    selectedServiceTypeId = maps.serviceIdToServiceTypeIdMap[selectedSubService.service_id];
  } else if (selectedServiceKey && maps.serviceIdToServiceTypeIdMap[selectedServiceKey]) {
    selectedServiceTypeId = maps.serviceIdToServiceTypeIdMap[selectedServiceKey];
  }

  let selectedServiceIds: string[] = [];
  if (selectedSubService?.service_id) {
    selectedServiceIds = [selectedSubService.service_id];
  } else if (selectedServiceTypeId) {
    selectedServiceIds = maps.serviceTypeToServiceIdsMap[selectedServiceTypeId] ?? [];
  } else if (selectedServiceKey) {
    selectedServiceIds = [selectedServiceKey];
  }

  const selectedServiceIdPayload: string | string[] =
    selectedServiceIds.length === 1 ? selectedServiceIds[0] : selectedServiceIds;

  return {
    selectedServiceTypeId,
    selectedServiceTypeName: selectedServiceTypeId ? maps.serviceTypeNamesByIdMap[selectedServiceTypeId] ?? "" : "",
    selectedSubService,
    selectedServiceIds,
    selectedServiceIdPayload,
  };
};

export const normalizeSelectionValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((id): id is string => typeof id === "string" && id.trim() !== "");
  }

  if (typeof value === "string" && value.trim() !== "") {
    return [value];
  }

  return [];
};

export const findDefaultSelectedSubServiceItem = (
  value: unknown,
  selectItems: MinimalSelectItem[] = [],
): MinimalSelectItem | undefined => {
  const normalizedValues = normalizeSelectionValues(value);
  return selectItems.find(
    (item) => normalizedValues.includes(item.id) || normalizedValues.includes(item.title),
  );
};

export const resolveSubServiceNameFromSelection = (
  value: unknown,
  options: {
    maps: BookingServiceMaps;
    selectItems?: MinimalSelectItem[];
  },
): string => {
  const selectedValue = normalizeSelectionValues(value)[0];
  if (!selectedValue) return "";

  const subServiceNameFromMap = options.maps.subServiceByIdMap[selectedValue]?.sub_service_name;
  if (subServiceNameFromMap) return subServiceNameFromMap;

  const selectedOption = options.selectItems?.find(
    (item) => item.id === selectedValue || item.title === selectedValue,
  );
  return selectedOption?.title ?? "";
};

export const VACCINATION_SERVICE_ID = "d83fdcb0-66a6-4079-adb4-4d15ec984b44";

export const isVaccinationServiceType = (serviceId?: string | null): boolean =>
  serviceId === VACCINATION_SERVICE_ID;

export const VIDEO_CONSULTATION_SERVICE_ID = "42332401-7656-461e-81ee-7da6b889dafb";

export const isVideoConsultationServiceType = (serviceId?: string | null): boolean =>
  serviceId === VIDEO_CONSULTATION_SERVICE_ID;

export const DIAGNOSTICS_SERVICE_TYPE_ID = "1949f98f-74a4-4e97-8344-ac2d59c22f6e";
// Pathology, X-Ray, Ultrasound and Emergency services under the Diagnostics type.
export const DIAGNOSTICS_SERVICE_IDS = [
  "84dfe271-9121-452b-b2c5-7919ad0cc434",
  "df98a7ee-7c34-45fc-8032-f5b56766da21",
  "5087c85b-c251-4dce-9ece-19c11b505230",
  "b2819ce1-fecd-4f2b-bba8-2d621c77f992",
];

// Legacy: Diagnostic Appointment sub-service and the Ultrasound type; Diagnostics type replaces both.
export const ADDON_DISCOUNT_SUBSERVICE_IDS = ["550b422b-ebe0-4809-b212-32b2d14ae639"];
export const ADDON_DISCOUNT_SERVICE_TYPE_IDS = ["1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed", DIAGNOSTICS_SERVICE_TYPE_ID];

// Legacy diagnostic sub-service -> its Diagnostics service type replacement.
export const LEGACY_DIAGNOSTIC_SUB_SERVICE_REPLACEMENTS: Record<string, string> = {
  "9105cc3b-559a-491d-aa53-02d8c5d49bfb": "63921327-5957-44bb-a623-6e253f5d7354", // Ultrasound -> Diagnostic Ultrasound
  "237325a2-232a-4418-a958-197db9c1237d": "63921327-5957-44bb-a623-6e253f5d7354", // Pregnancy USG -> Diagnostic Ultrasound
  "d1a9ad86-4549-11ed-af80-d1c5db9bbce2": "a92f13c6-9483-4dca-8a34-411a6f0569a9", // Consult for X-Ray -> Diagnostic X-Ray
  "4188851f-0218-4885-865e-e96e1a505d0f": "a92f13c6-9483-4dca-8a34-411a6f0569a9", // Free Consult For X-Ray -> Diagnostic X-Ray
  "550b422b-ebe0-4809-b212-32b2d14ae639": "7cfa0292-a703-4062-af11-41c119c78a92", // Diagnostic Appointment -> Diagnostic Pathology
  "d1a9ad87-4549-11ed-af80-d1c5db9bbce2": "7cfa0292-a703-4062-af11-41c119c78a92", // Consult for Diagnostics -> Diagnostic Pathology
};

// Diagnostics replacement sub-service -> its service.
const DIAGNOSTICS_SUB_SERVICE_SERVICE_IDS: Record<string, string> = {
  "63921327-5957-44bb-a623-6e253f5d7354": "5087c85b-c251-4dce-9ece-19c11b505230", // Diagnostic Ultrasound
  "a92f13c6-9483-4dca-8a34-411a6f0569a9": "df98a7ee-7c34-45fc-8032-f5b56766da21", // Diagnostic X-Ray
  "7cfa0292-a703-4062-af11-41c119c78a92": "84dfe271-9121-452b-b2c5-7919ad0cc434", // Diagnostic Pathology
};

// A prefilled legacy diagnostic sub-service that is no longer offered becomes its replacement;
// while the legacy one is still listed (or the replacement is not), the id is kept as is.
export const resolveLegacyDiagnosticSubServiceId = <T extends string | null | undefined>(
  subServiceId: T,
  isListed: (id: string) => boolean,
): T | string => {
  const replacement = subServiceId ? LEGACY_DIAGNOSTIC_SUB_SERVICE_REPLACEMENTS[subServiceId] : undefined;
  if (!subServiceId || !replacement || isListed(subServiceId) || !isListed(replacement)) return subServiceId;
  return replacement;
};

// Booking a legacy diagnostic appointment again (Book Again, next-visit prefill) books its Diagnostics
// replacement, even while the legacy one is still listed. A reschedule must keep its own service.
export const toDiagnosticsReplacement = (
  serviceId: string,
  subServiceId: string,
): { serviceId: string; subServiceId: string } => {
  const replacement = subServiceId ? LEGACY_DIAGNOSTIC_SUB_SERVICE_REPLACEMENTS[subServiceId] : undefined;
  if (!replacement) return { serviceId, subServiceId };
  return { serviceId: DIAGNOSTICS_SUB_SERVICE_SERVICE_IDS[replacement], subServiceId: replacement };
};

export const isAddOnDiscountEnabled = (ids?: {subServiceId?: string ;serviceTypeId?: string;}): boolean =>
  (!!ids?.subServiceId && ADDON_DISCOUNT_SUBSERVICE_IDS.includes(ids.subServiceId)) ||
  (!!ids?.serviceTypeId && ADDON_DISCOUNT_SERVICE_TYPE_IDS.includes(ids.serviceTypeId));

export const getVaccineId = (item: IBookingCartPreviewCartItem): string =>
  item.vaccine_id || item.id;

export const getPreviewVaccineItems = (
  cartItems?: IBookingCartPreviewCartItem[] | null,
): IBookingCartPreviewCartItem[] => (cartItems ?? []).filter((item) => item.type === "vaccine");

interface IBuildVaccineCartItemsArgs {
  previewVaccineItems: IBookingCartPreviewCartItem[];
  selectedVaccineIds: string[];
  vaccineCouponMap?: Record<string, string> | null;
  forCreate?: boolean;
}

export const buildVaccineCartItems = ({
  previewVaccineItems,
  selectedVaccineIds,
  vaccineCouponMap,
  forCreate = false,
}: IBuildVaccineCartItemsArgs): IBookingCartPreviewRequestCartItem[] => {
  const selectedSet = new Set(selectedVaccineIds);
  const couponMap = vaccineCouponMap ?? {};

  return previewVaccineItems
    .map((item) => {
      const vaccineId = getVaccineId(item);
      const isSelected = selectedSet.has(vaccineId);
      const couponId = couponMap[vaccineId];
      return {
        type: "vaccine" as const,
        id: vaccineId,
        qty: isSelected ? 1 : 0,
        ...(couponId ? { coupon_id: couponId } : {}),
        vaccination_details: item.vaccination_details ?? null,
      };
    })
    .filter((item) => (forCreate ? item.qty >= 1 : true));
};

export const DIAGNOSTIC_CART_ITEM_TYPE = "diagnostic";

export const getPreviewDiagnosticItems = (
  cartItems?: IBookingCartPreviewCartItem[] | null,
): IBookingCartPreviewCartItem[] =>
  (cartItems ?? []).filter((item) => item.type === DIAGNOSTIC_CART_ITEM_TYPE);

export const getSummaryDiagnosticItems = (
  summaryData?: IBookingSummaryResponseData | null,
): IBookingSummaryItem[] => summaryData?.addons?.diagnostics ?? [];

// Summary diagnostics carry the same fields as a preview cart line, so they are normalized to
// the cart-item shape and reused by every "Items in Cart" list.
export const toDiagnosticCartItem = (item: IBookingSummaryItem): IBookingCartPreviewCartItem => ({
  id: item.id,
  type: DIAGNOSTIC_CART_ITEM_TYPE,
  qty: Number(item.qty ?? 1),
  max_qty: Number(item.max_qty ?? item.qty ?? 1),
  name: item.name,
  price: Number(item.price ?? 0),
  discount: Number(item.discount ?? Math.max(0, Number(item.price ?? 0) - Number(item.final_price ?? 0))),
  final_price: Number(item.final_price ?? item.price ?? 0),
  discount_percent: Number(item.discount_percent ?? 0),
  duration: item.duration ?? null,
  description: item.description ?? null,
  image_url: item.image_url?.replace(/^\{|\}$/g, "") || undefined,
  coupon_id: item.coupon_id ?? null,
  billing_status: item.billing_status,
  previously_added: Boolean(item.previously_added),
});

interface IResolveDiagnosticCartItemsArgs {
  previewCartItems?: IBookingCartPreviewCartItem[] | null;
  summaryData?: IBookingSummaryResponseData | null;
}

// Preview lines win over summary lines (they carry the live pricing/coupon for the cart being
// previewed), summary-only diagnostics are appended so nothing already on the booking is lost.
export const resolveDiagnosticCartItems = ({
  previewCartItems,
  summaryData,
}: IResolveDiagnosticCartItemsArgs): IBookingCartPreviewCartItem[] => {
  const previewDiagnostics = getPreviewDiagnosticItems(previewCartItems);
  const previewDiagnosticIds = new Set(previewDiagnostics.map((item) => item.id));
  const summaryDiagnostics = getSummaryDiagnosticItems(summaryData)
    .filter((item) => !previewDiagnosticIds.has(item.id))
    .map(toDiagnosticCartItem);

  return [...previewDiagnostics, ...summaryDiagnostics];
};

// Diagnostics are attached to a booking by the lab/EMR flows and VMS has no catalog to pick
// them from, so the client echoes every diagnostic already on the booking back into cart_items.
// Leaving them out would drop them from the booking on the next cart update/reschedule.
// Already-billed diagnostics are skipped, the same way a billed membership is kept out of the
// cart payload — they are shown in the cart but must not be charged again.
export const buildDiagnosticCartItems = (
  args: IResolveDiagnosticCartItemsArgs,
): IBookingCartPreviewRequestCartItem[] =>
  resolveDiagnosticCartItems(args)
    .filter((item) => String(item.billing_status ?? "").toUpperCase() !== "BILLED")
    .map((item) => ({
      type: DIAGNOSTIC_CART_ITEM_TYPE,
      id: item.id,
      qty: Math.max(1, Number(item.qty ?? 1)),
      ...(item.coupon_id ? { coupon_id: item.coupon_id } : {}),
    }));
